import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '@/lib/supabase';

const PUBLIC_VAPID_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const savePushRow = async (email: string, subscription: any) => {
  await supabase.from('push_subscriptions').upsert(
    {
      email: email.trim().toLowerCase(),
      subscription,
      user_agent: navigator.userAgent,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'email' }
  );
};

export const registerPushForUser = async (email: string) => {
  if (!email) return;

  const cleanEmail = email.trim().toLowerCase();

  if (Capacitor.isNativePlatform()) {
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return;

    await PushNotifications.removeAllListeners();

    await PushNotifications.addListener('registration', async (token) => {
      await savePushRow(cleanEmail, {
        type: 'fcm',
        token: token.value,
        platform: Capacitor.getPlatform(),
      });
      console.log('[Push] Native FCM token saved');
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.warn('[Push] Native registration failed:', err);
    });

    await PushNotifications.register();
    return;
  }

  if (!PUBLIC_VAPID_KEY) return;
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return;

  const registration = await navigator.serviceWorker.register('/sw.js');
  const existing = await registration.pushManager.getSubscription();

  if (existing) {
    await existing.unsubscribe().catch(() => {});
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
  });

  await savePushRow(cleanEmail, {
    type: 'webpush',
    subscription,
  });
};
